import React, { useEffect } from 'react';
import { extendContext, createElementObject, createPathComponent } from '@react-leaflet/core';
import L, { Control, divIcon, DivIconOptions, DomUtil, Icon, Layer, LayerGroup, LeafletMouseEventHandlerFn, Map } from 'leaflet';
import 'leaflet';
import 'leaflet.markercluster';
import './MarkerCluster.css';
import { useMap } from 'react-leaflet';
import markerIconUrl from 'leaflet/dist/images/marker-icon.png';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default as any).prototype._getIconUrl;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ClusterType = { [key in string]: any };

type ClusterEvents = {
  onClick?: LeafletMouseEventHandlerFn;
  onDblClick?: LeafletMouseEventHandlerFn;
  onMouseDown?: LeafletMouseEventHandlerFn;
  onMouseUp?: LeafletMouseEventHandlerFn;
  onMouseOver?: LeafletMouseEventHandlerFn;
  onMouseOut?: LeafletMouseEventHandlerFn;
  onContextMenu?: LeafletMouseEventHandlerFn;
};

type ControlledLayer = {
  addLayer(layer: Layer): void;
  removeLayer(layer: Layer): void;
};

type LeafletContextInterface = Readonly<{
  __version: number;
  map: Map;
  layerContainer?: ControlledLayer | LayerGroup;
  layersControl?: Control.Layers;
  overlayContainer?: Layer;
  pane?: string;
}>;

type MarkerClusterControl = L.MarkerClusterGroupOptions & {
  children?: React.ReactNode; // Make children optional
} & ClusterEvents;

type MarkerClusterControlPropsReturn = {
  clusterProps: ClusterType;
  clusterEvents: ClusterType;
};

type CreateClusterCustomMarkerType = ReturnType<typeof createElementObject<L.MarkerClusterGroup>>;

function getPropsAndEvents(props: MarkerClusterControl): MarkerClusterControlPropsReturn {
  let clusterProps: ClusterType = {};
  let clusterEvents: ClusterType = {};

  Object.entries(props).forEach(([propName, prop]) => {
    if (propName !== 'children') {
      if (propName.startsWith('on')) {
        clusterEvents = { ...clusterEvents, [propName]: prop };
      } else {
        clusterProps = { ...clusterProps, [propName]: prop };
      }
    }
  });
  return { clusterProps, clusterEvents };
}

function createMarkerClusterGroup(props: MarkerClusterControl, context: LeafletContextInterface): CreateClusterCustomMarkerType {
  const { clusterProps, clusterEvents } = getPropsAndEvents(props);
  const markerClusterGroup = new L.MarkerClusterGroup(clusterProps);
  Object.entries(clusterEvents).forEach(([eventAsProp, callback]) => {
    const clusterEvent = `cluster${eventAsProp.substring(2).toLowerCase()}`;
    markerClusterGroup.on(clusterEvent, callback);
  });
  return createElementObject(markerClusterGroup, extendContext(context, { layerContainer: markerClusterGroup }));
}

export const MarkerClusterGroup = createPathComponent<L.MarkerClusterGroup, MarkerClusterControl>(createMarkerClusterGroup);

export function MapPlaceholder(): React.JSX.Element {
  return (
    <p>
      Map of London. <noscript>You need to enable JavaScript to see this map.</noscript>
    </p>
  );
}

export function ControlPanel({ testId }: { testId: string }): React.JSX.Element {
  const map = useMap();
  map.invalidateSize();

  useEffect(() => {
    const mapContainer = map.getContainer();
    mapContainer.setAttribute('data-testid', testId);

    // Apply fullscreen styles to the container
    mapContainer.style.position = 'fixed';
    mapContainer.style.top = '0';
    mapContainer.style.left = '0';
    mapContainer.style.width = '100vw'; // Ensure full screen width
    mapContainer.style.height = '100vh'; // Ensure full screen height
    mapContainer.style.zIndex = '1000'; // Make sure it's on top

    // Make sure map recalculates its size after going fullscreen
    map.invalidateSize();

    // Add the custom control (fullscreen button, etc.)
    const CustomButton = Control.extend({
      options: {
        position: 'topleft',
      },
      onAdd: function () {
        const container = DomUtil.create('div', 'leaflet-bar leaflet-control');
        const fullscreenBtn = DomUtil.create('a', 'leaflet-control-fullscreenBtn', container);
        const zoomExitBtn = DomUtil.create('a', 'leaflet-control-zoom-exit', container);

        zoomExitBtn.innerHTML = `...`; // Your SVG content here
        fullscreenBtn.innerHTML = `...`; // Your SVG content here

        fullscreenBtn.title = 'Full screen';
        zoomExitBtn.title = 'Zoom exit';

        return container;
      },
    });

    map.addControl(new CustomButton());
  }, [map, testId]);

  return <></>; // Return an empty fragment or other JSX component to ensure non-null return
}

const getTextWidth = (text: string, minWidth = 35, maxWidth = 55, charWidth = 14): number => {
  let baseSize = Math.max(charWidth * Math.exp(text?.length * 0.3), minWidth);

  if (text?.length <= 3 && text?.length > 1) baseSize += text?.length * 1.5;

  return Math.min(baseSize, maxWidth);
};

// TODO: this one can now be replaced by createPinIcon i think?
export const createMarkerIcon = (args: { iconUrl?: string }) => {
  return new Icon({
    iconUrl: args.iconUrl || markerIconUrl,
    iconSize: args.iconUrl ? [38, 38] : [25, 41],
    iconAnchor: args.iconUrl ? [5, 36] : [12, 36],
    popupAnchor: [1, -34],
  });
};

interface CreateMarkerOptions extends Partial<DivIconOptions> {
  link?: string;
  text?: string;
}

export const createPinIcon = (args?: { className?: string; link?: string }): L.DivIcon => {
  return createDivMarkerIcon({
    ...args,
    iconUrl: markerIconUrl,
    iconSize: [27, 41],
    iconAnchor: [13.5, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [15, -25],
    className: args?.className,
  });
};

export const createDivMarkerIcon = (args: CreateMarkerOptions): L.DivIcon => {
  if (!args.html) {
    if (args.iconUrl) args.html = args.link ? `<a href=${args.link}><img src=${markerIconUrl} /></a>` : `<img src=${markerIconUrl} />`;
    else
      args.html = args.link
        ? `<a data-testid="circular-marker" href=${args.link}>${args.text ?? ' '}</a>`
        : `<span data-testid="circular-marker">${args.text ?? ''}</span>`;
  }

  return divIcon({
    tooltipAnchor: [15, 0],
    ...args,
    iconSize: args.iconSize ?? [getTextWidth(args.text ?? ''), getTextWidth(args.text ?? '')],
  });
};

export const createClusterCustomMarker = (cluster: { getAllChildMarkers: () => L.Marker[] }): L.DivIcon => {
  const totalCustomPropertyValue = cluster.getAllChildMarkers().reduce((acc, marker) => {
    return acc + Number(marker.options.title);
  }, 0);

  return createDivMarkerIcon({ text: totalCustomPropertyValue.toString(), className: 'reader-cluster-icon' });
};

// Add OpenStreetMap Tile Layer to Map
export function OpenStreetMapTileLayer(): React.JSX.Element {
  const map = useMap();

  useEffect(() => {
    // Add OpenStreetMap Tile Layer
    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    });

    osmLayer.addTo(map);

    return () => {
      map.removeLayer(osmLayer);
    };
  }, [map]);

  return <></>;
}
